import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * XEA Button system — pill-shaped, premium-feeling, with built-in lift on hover.
 *
 *  variant:
 *   - default     : stark black pill, white text (primary CTA)
 *   - accent      : citrus highlight (use for "Generate" / "Upgrade")
 *   - coral       : coral accent (destructive-positive, e.g. "Upgrade now")
 *   - outline     : transparent with ink border
 *   - ghost       : minimal hover-tinted
 *   - link        : underlined link button
 *   - destructive : red destructive action
 *   - secondary   : muted gray surface
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-all duration-300 will-change-transform " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring " +
  "disabled:pointer-events-none disabled:opacity-50 " +
  "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 " +
  "hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99]",
  {
    variants: {
      variant: {
        default:
          "bg-ink-950 text-white shadow-[0_8px_24px_rgba(10,10,10,0.18)] hover:shadow-[0_14px_36px_rgba(10,10,10,0.24)]",
        accent:
          "bg-citrus-500 text-ink-950 shadow-citrus hover:bg-citrus-600",
        coral:
          "bg-coral-500 text-white shadow-coral hover:bg-coral-600",
        destructive:
          "bg-danger text-white shadow-[0_8px_24px_rgba(255,59,48,0.25)] hover:opacity-95",
        outline:
          "border border-ink-200 bg-white text-ink-950 hover:bg-ink-50 hover:border-ink-300",
        secondary:
          "bg-ink-100 text-ink-950 hover:bg-ink-200",
        ghost:
          "hover:bg-ink-100 text-ink-950",
        link:
          "text-ink-950 underline-offset-4 hover:underline rounded-none px-0 h-auto",
      },
      size: {
        default: "h-11 px-6 text-sm",
        sm: "h-9 px-4 text-xs",
        lg: "h-14 px-8 text-base",
        xl: "h-16 px-10 text-base",
        icon: "h-11 w-11 [&_svg]:size-5",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
);

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };
